/**
 * Settings' Labels (migration 0090; P4 of the #55 plan, §D.2 and §D.5.3).
 *
 * A label is a free tag, many to a line, never required and never totalled
 * beside another label. Each belongs to ONE fiscal year, so this section shows
 * one year at a time, the current one first, with the year either side a
 * press away. Roll Over copies the labels ticked into the next year, each
 * naming the one it came from, which is what lets a report read one label
 * across years. A closed year is shown read-only, and says why.
 *
 * Anyone signed in reads them; whoever edits entries makes, changes and rolls
 * them over (the API's rule): a label adds no structure the books depend on.
 */

import { useEffect, useState } from 'react';
import { ApiError, LABEL_COLOURS, api, type Label, type LabelColour, type LabelYear } from '../api.js';
import { useCan } from '../auth/AuthProvider.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

const COLOUR_NAME: Record<LabelColour, string> = {
  grey: 'Grey',
  red: 'Red',
  orange: 'Orange',
  yellow: 'Yellow',
  green: 'Green',
  teal: 'Teal',
  blue: 'Blue',
  purple: 'Purple',
};

/** A label as a chip: its name, with its colour as the border. */
export function LabelChip(props: { name: string; colour: string; archived?: boolean; onRemove?: () => void; removeLabel?: string }) {
  return (
    <span className="label-chip" data-colour={props.colour}>
      {props.name}
      {props.archived && ' (Archived)'}
      {props.onRemove && (
        <button type="button" className="label-chip-remove" aria-label={props.removeLabel ?? `Remove ${props.name}`} onClick={props.onRemove}>
          ×
        </button>
      )}
    </span>
  );
}

interface FormValues {
  name: string;
  group: string;
  colour: LabelColour;
}

function LabelForm(props: {
  initial: FormValues;
  submitLabel: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (values: FormValues) => void;
}) {
  const [values, setValues] = useState<FormValues>(props.initial);
  return (
    <div className="new-account fund-form" role="group" aria-label={props.submitLabel}>
      <div className="field-row">
        <label className="field">
          <span>Name</span>
          <input
            value={values.name}
            maxLength={60}
            onChange={(event) => {
              const name = event.target.value;
              setValues((current) => ({ ...current, name }));
            }}
          />
        </label>
        <label className="field">
          <span>Group</span>
          <input
            value={values.group}
            maxLength={60}
            placeholder="Optional"
            onChange={(event) => {
              const group = event.target.value;
              setValues((current) => ({ ...current, group }));
            }}
          />
        </label>
        <label className="field field-short">
          <span>Colour</span>
          <select
            value={values.colour}
            onChange={(event) => {
              const colour = event.target.value as LabelColour;
              setValues((current) => ({ ...current, colour }));
            }}
          >
            {LABEL_COLOURS.map((colour) => (
              <option key={colour} value={colour}>
                {COLOUR_NAME[colour]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <button type="button" disabled={props.busy} onClick={() => props.onSubmit(values)}>
        {props.submitLabel}
      </button>
      <button type="button" className="link" onClick={props.onCancel}>
        Cancel
      </button>
    </div>
  );
}

export function LabelsSection() {
  const can = useCan();
  const edits = can('edit_entries');
  const [year, setYear] = useState<number | undefined>(undefined);
  const [data, setData] = useState<LabelYear | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [rolling, setRolling] = useState(false);
  const [ticked, setTicked] = useState<Set<string>>(new Set());

  useEffect(() => {
    let live = true;
    setData(null);
    api
      .listLabels(year)
      .then((result) => {
        if (live) setData(result);
      })
      .catch((caught: unknown) => {
        if (live) setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the labels.');
      });
    return () => {
      live = false;
    };
  }, [year]);

  function replace(label: Label) {
    setData((current) =>
      current === null
        ? current
        : {
            ...current,
            labels: current.labels.some((candidate) => candidate.id === label.id)
              ? current.labels.map((candidate) => (candidate.id === label.id ? label : candidate))
              : [...current.labels, label],
          },
    );
  }

  async function act(run: () => Promise<{ label: Label }>, done: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { label } = await run();
      replace(label);
      setEditing(null);
      setNotice(done);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save the label.');
    } finally {
      setBusy(false);
    }
  }

  async function rollOver() {
    if (data === null) return;
    if (ticked.size === 0) {
      setError(`Tick the labels to carry into fiscal ${data.fiscalYear + 1} first.`);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.rollOverLabels(data.fiscalYear, [...ticked]);
      const into = new Map(result.labels.map((label) => [label.rolledFromLabelId, label.id]));
      setData({ ...data, labels: data.labels.map((label) => (into.has(label.id) ? { ...label, rolledIntoLabelId: into.get(label.id)! } : label)) });
      setTicked(new Set());
      setRolling(false);
      setNotice(`Rolled ${result.labels.length} ${result.labels.length === 1 ? 'label' : 'labels'} over into fiscal ${result.fiscalYear}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not roll the labels over.');
    } finally {
      setBusy(false);
    }
  }

  const writable = edits && data !== null && !data.closed;
  const rollable = data?.labels.filter((label) => label.archivedAt === null && label.rolledIntoLabelId === null) ?? [];

  return (
    <section className="settings-section" aria-labelledby="settings-labels">
      <h2 id="settings-labels">Labels</h2>
      <p className="muted">
        Free tags for finding lines again: an insurance claim, warranty work, one event&rsquo;s costs.{' '}
        <InfoNote label="What A Label Is">
          A line can carry several labels, on any account, and none is ever required. One label&rsquo;s total is exact; several
          labels side by side do not add up, since a line can carry more than one, so no report totals them together. Each label
          belongs to one fiscal year. Roll it over to carry it into the next year: the new label names the one it came from, and
          the Labels report can read the two as one. A label of a closed year does not change, and a line&rsquo;s labels do not
          change once the books are closed through its date.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {data === null ? (
        !loadError && <p className="muted">Loading…</p>
      ) : (
        <>
          <div className="row">
            <button type="button" className="link" onClick={() => setYear(data.fiscalYear - 1)}>
              ‹ Fiscal {data.fiscalYear - 1}
            </button>
            <strong>Fiscal {data.fiscalYear}</strong>
            <button type="button" className="link" onClick={() => setYear(data.fiscalYear + 1)}>
              Fiscal {data.fiscalYear + 1} ›
            </button>
          </div>
          {data.closed && <p className="muted">Fiscal {data.fiscalYear} is closed, so its labels do not change. Reopen the year in Books to change them.</p>}

          {data.labels.length === 0 ? (
            <p className="muted">No labels in fiscal {data.fiscalYear}.</p>
          ) : (
            <div className="table-scroll">
              <table className="lines">
                <thead>
                  <tr>
                    {rolling && <th aria-label="Roll over" />}
                    <th>Label</th>
                    <th>Group</th>
                    <th className="num">Lines</th>
                    <th>Status</th>
                    {writable && <th />}
                  </tr>
                </thead>
                <tbody>
                  {data.labels.map((label) => (
                    <tr key={label.id}>
                      {rolling && (
                        <td>
                          {rollable.includes(label) && (
                            <input
                              type="checkbox"
                              aria-label={`Roll over ${label.name}`}
                              checked={ticked.has(label.id)}
                              onChange={(event) => {
                                const on = event.target.checked;
                                setTicked((current) => {
                                  const next = new Set(current);
                                  if (on) next.add(label.id);
                                  else next.delete(label.id);
                                  return next;
                                });
                              }}
                            />
                          )}
                        </td>
                      )}
                      <td>
                        <LabelChip name={label.name} colour={label.colour} />
                      </td>
                      <td>{label.group ?? ''}</td>
                      <td className="num">{label.lineCount}</td>
                      <td>
                        {label.archivedAt !== null && <span className="tag">Archived</span>}{' '}
                        {label.rolledIntoLabelId !== null && <span className="tag">Rolled Over</span>}
                      </td>
                      {writable && (
                        <td className="line-actions">
                          <button type="button" className="link" aria-label={`Change ${label.name}`} onClick={() => setEditing(label.id)}>
                            Change
                          </button>
                          <button
                            type="button"
                            className="link"
                            disabled={busy}
                            aria-label={`${label.archivedAt === null ? 'Archive' : 'Restore'} ${label.name}`}
                            onClick={() =>
                              void act(
                                () => api.updateLabel(label.id, { archived: label.archivedAt === null }),
                                `${label.archivedAt === null ? 'Archived' : 'Restored'} ${label.name}`,
                              )
                            }
                          >
                            {label.archivedAt === null ? 'Archive' : 'Restore'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {writable &&
            editing !== null &&
            (() => {
              const label = data.labels.find((candidate) => candidate.id === editing);
              if (editing !== 'new' && label === undefined) return null;
              return (
                <LabelForm
                  key={editing}
                  initial={label ? { name: label.name, group: label.group ?? '', colour: label.colour } : { name: '', group: '', colour: 'grey' }}
                  submitLabel={label ? 'Save Label' : 'Add Label'}
                  busy={busy}
                  onCancel={() => setEditing(null)}
                  onSubmit={(values) => {
                    // A control never does nothing: an empty name answers with a sentence.
                    if (values.name.trim() === '') {
                      setError('A label has a name.');
                      return;
                    }
                    void act(
                      () =>
                        label
                          ? api.updateLabel(label.id, { name: values.name, group: values.group.trim() === '' ? null : values.group, colour: values.colour })
                          : api.createLabel({
                              fiscalYear: data.fiscalYear,
                              name: values.name,
                              group: values.group.trim() === '' ? null : values.group,
                              colour: values.colour,
                            }),
                      label ? `Saved ${values.name}` : `Added ${values.name}`,
                    );
                  }}
                />
              );
            })()}

          {edits && editing === null && (
            <div className="row">
              {writable && (
                <button type="button" className="link" onClick={() => setEditing('new')}>
                  New Label
                </button>
              )}
              {rollable.length > 0 &&
                (rolling ? (
                  <>
                    <button type="button" disabled={busy} onClick={() => void rollOver()}>
                      Roll Over To Fiscal {data.fiscalYear + 1}
                    </button>
                    <button type="button" className="link" onClick={() => setRolling(false)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <button type="button" className="link" onClick={() => setRolling(true)}>
                    Choose Labels To Roll Over
                  </button>
                ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
